import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import { getCart, saveCart } from "@/utils/cart";
import { X, ChevronLeft, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mobile OTP needs an SMS provider; none is connected yet, so only email login is offered.
const MOBILE_LOGIN_AVAILABLE = false;

export default function CustomerLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = identifier, 2 = otp
  const [useEmail, setUseEmail] = useState(!MOBILE_LOGIN_AVAILABLE);
  const [phone, setPhone] = useState("");
  const [emailVal, setEmailVal] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loginSettings, setLoginSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/mobile-account-settings`).then(r => setLoginSettings(r.data?.login_page || null)).catch(() => {});
  }, []);

  const identifier = useEmail ? emailVal.trim() : (phone.trim() ? `+91${phone.trim()}` : "");

  const startCooldown = () => {
    setResendCooldown(30);
    const t = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(t); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    const id = identifier;
    if (!id || (useEmail && !emailVal.includes("@")) || (!useEmail && phone.length !== 10)) {
      toast.error(useEmail ? "Enter a valid email address" : "Enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-otp`, { identifier: id });
      toast.success(res.data.via === "email" ? "OTP sent to your email" : "OTP sent to your mobile");
      setStep(2);
      startCooldown();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, {
        identifier,
        otp,
        name: name || undefined,
        cart: getCart(),
      });
      localStorage.setItem("it_customer_token", res.data.token);
      localStorage.setItem("it_customer_user", JSON.stringify({
        id: res.data.id,
        email: res.data.email,
        name: res.data.name,
        role: res.data.role,
      }));
      // Restore merged cart from server
      if (res.data.cart && res.data.cart.length > 0) {
        saveCart(res.data.cart);
      }
      // Sync local wishlist with backend
      const localWishlist = (() => {
        try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]"); } catch { return []; }
      })();
      if (localWishlist.length > 0) {
        try {
          const syncRes = await axios.post(
            `${API}/customer/wishlist/sync`,
            { product_ids: localWishlist },
            { headers: { Authorization: `Bearer ${res.data.token}` } }
          );
          localStorage.setItem("it_wishlist", JSON.stringify(syncRes.data.wishlist || localWishlist));
          window.dispatchEvent(new Event("wishlist-updated"));
        } catch { /* silent — local wishlist unchanged */ }
      }
      toast.success(`Welcome, ${res.data.name}!`);
      navigate("/account");
    } catch (e) {
      const msg = e.response?.data?.detail || "Invalid OTP. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {/* ═══════════════════════ MOBILE LOGIN ═══════════════════════ */}
    <div className="block md:hidden min-h-screen bg-[#f1f3f6]" data-testid="mobile-login-page">
      {/* Brand header */}
      <div className="px-4 py-5" style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" }}>
        <div className="flex items-center justify-between">
          <div>
              <BrandLogo size="md" textVariant="light" />
              <p className="text-orange-100 text-xs mt-0.5">Products for a Better Everyday</p>
            </div>
          <button onClick={() => navigate("/account")} className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="mobile-login-close-btn">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-t-3xl -mt-3 min-h-[calc(100vh-80px)] px-6 pt-8 pb-10">
        {step === 1 ? (
          <>
            <h1 className="text-xl font-black text-slate-900 mb-1">
              {loginSettings?.title || "Log in for the best experience"}
            </h1>
            <p className="text-sm text-slate-500 mb-8">
              {loginSettings?.subtitle || "Enter your email address to continue"}
            </p>

            <form onSubmit={handleSendOTP} className="space-y-5">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 block">Email Address</label>
                <div className="flex items-center border-2 border-slate-200 focus-within:border-orange-400 rounded-xl overflow-hidden transition-colors">
                  <div className="pl-4 pr-2 flex items-center">
                    <Mail className="w-4 h-4 text-slate-400" />
                  </div>
                  <input
                    type="email"
                    value={emailVal}
                    onChange={e => setEmailVal(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-2 py-3.5 text-sm text-slate-800 focus:outline-none bg-transparent"
                    data-testid="email-input"
                    autoFocus
                  />
                </div>
              </div>

              <p className="text-[11px] text-slate-400 leading-relaxed">
                {loginSettings?.terms_text || "By continuing, you agree to our Terms of Use and Privacy Policy"}
              </p>

              <button
                type="submit"
                disabled={loading || !emailVal.includes("@")}
                data-testid="send-otp-btn"
                className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-200 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
              >
                {loading
                  ? <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />Sending…</>
                  : <>{loginSettings?.cta_text || "Continue"} →</>}
              </button>
            </form>
          </>
        ) : (
          <>
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-500 mb-6 hover:text-slate-700 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            <h1 className="text-xl font-black text-slate-900 mb-1">Verify OTP</h1>
            <p className="text-sm text-slate-500 mb-2">OTP sent to <strong className="text-slate-700">{emailVal}</strong></p>

            <form onSubmit={handleVerifyOTP} className="space-y-5 mt-6">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 block">6-Digit OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="• • • • • •"
                  className="w-full px-4 py-4 border-2 border-slate-200 focus:border-orange-400 rounded-xl text-2xl font-black tracking-[0.5em] text-center focus:outline-none transition-colors"
                  data-testid="otp-input"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 block">
                  Your Name <span className="text-slate-400 font-normal normal-case">(optional)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3.5 border-2 border-slate-200 focus:border-orange-400 rounded-xl text-sm focus:outline-none transition-colors"
                  data-testid="name-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                data-testid="verify-btn"
                className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-200 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
              >
                {loading
                  ? <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />Verifying…</>
                  : "Verify & Login →"}
              </button>

              <p className="text-center text-xs text-slate-400">
                Didn't receive OTP?{" "}
                {resendCooldown > 0
                  ? <span className="text-slate-400">Resend in {resendCooldown}s</span>
                  : <button onClick={handleSendOTP} disabled={loading} data-testid="resend-btn" className="text-orange-500 font-bold hover:underline">Resend OTP</button>}
              </p>
            </form>
          </>
        )}
      </div>
    </div>

    {/* ═══════════════════════ DESKTOP LOGIN ══════════════════════ */}
    <div className="hidden md:block min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-[750px] rounded-xl overflow-hidden shadow-2xl flex" style={{ minHeight: 480 }}>

        {/* ── Left: brand panel ─────────────────────────── */}
        <div className="hidden sm:flex flex-col justify-between w-[44%] flex-shrink-0 p-8 text-white"
          style={{ background: "linear-gradient(160deg, var(--brand) 0%, var(--brand-dark, #c45d1c) 100%)" }}>
          <div>
            <h1 className="text-2xl font-bold mb-2 leading-tight">Login</h1>
            <p className="text-sm text-white/80 leading-relaxed">
              Get access to your Orders,<br />
              Wishlist and Recommendations
            </p>
          </div>

          <div className="flex items-end justify-center pb-2">
            <svg width="180" height="160" viewBox="0 0 220 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="55" y="80" width="110" height="90" rx="8" fill="white" fillOpacity="0.15" />
              <rect x="65" y="90" width="90" height="70" rx="6" fill="white" fillOpacity="0.2" />
              <path d="M85 80 Q85 55 110 55 Q135 55 135 80" stroke="white" strokeWidth="6" strokeLinecap="round" fill="none" strokeOpacity="0.7" />
              <text x="90" y="135" fill="white" fontSize="22" fillOpacity="0.9">★★★</text>
              <rect x="35" y="120" width="24" height="24" rx="4" fill="white" fillOpacity="0.15" />
              <rect x="161" y="115" width="20" height="20" rx="4" fill="white" fillOpacity="0.15" />
              <circle cx="40" cy="70" r="8" fill="white" fillOpacity="0.12" />
              <circle cx="178" cy="85" r="6" fill="white" fillOpacity="0.12" />
            </svg>
          </div>
        </div>

        {/* ── Right: form panel ───────────────────────────── */}
        <div className="flex-1 bg-white flex flex-col justify-center px-8 py-10">

          {step === 1 ? (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">
                Log in for the best experience
              </h2>
              <p className="text-xs text-slate-400 mb-6">
                Access exclusive deals and track your orders
              </p>

              <form onSubmit={handleSendOTP} className="space-y-4">
                {!useEmail ? (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                      Enter Mobile Number
                    </label>
                    <div className="flex border border-slate-300 rounded-lg overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-200 transition-all">
                      <div className="flex items-center px-3 bg-slate-50 border-r border-slate-300 text-sm font-semibold text-slate-700 flex-shrink-0 gap-1">
                        <span className="text-base">🇮🇳</span> +91
                      </div>
                      <input
                        type="tel"
                        value={phone}
                        onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        placeholder="Mobile number"
                        className="flex-1 px-3 py-3 text-sm focus:outline-none bg-white"
                        data-testid="phone-input"
                        autoFocus
                      />
                    </div>
                    {MOBILE_LOGIN_AVAILABLE && (
                    <button type="button" onClick={() => setUseEmail(true)}
                      className="mt-2 text-xs text-blue-600 hover:underline font-medium">
                      Use Email-ID instead
                    </button>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                      Enter Email Address
                    </label>
                    <input
                      type="email"
                      value={emailVal}
                      onChange={e => setEmailVal(e.target.value)}
                      placeholder="Email address"
                      className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all"
                      data-testid="email-input"
                      autoFocus
                    />
                    {MOBILE_LOGIN_AVAILABLE && (
                    <button type="button" onClick={() => setUseEmail(false)}
                      className="mt-2 text-xs text-blue-600 hover:underline font-medium">
                      Use Mobile Number instead
                    </button>
                    )}
                  </div>
                )}

                <p className="text-[11px] text-slate-400 leading-relaxed">
                  By continuing, you agree to Importtrend's{" "}
                  <span className="text-blue-600 cursor-pointer hover:underline">Terms of Use</span> and{" "}
                  <span className="text-blue-600 cursor-pointer hover:underline">Privacy Policy</span>.
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="continue-btn"
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-300 text-white font-bold rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading
                    ? <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Sending OTP…</>
                    : "Continue"}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">OR</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <p className="text-center text-sm text-slate-500">
                New to Importtrend?{" "}
                <span className="text-blue-600 font-semibold cursor-pointer hover:underline">
                  Create an account
                </span>
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => { setStep(1); setOtp(""); }}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-6 transition-colors w-fit"
              >
                ← Change {useEmail ? "email" : "number"}
              </button>

              <h2 className="text-lg font-semibold text-slate-800 mb-1">
                Enter OTP
              </h2>
              <p className="text-xs text-slate-400 mb-6">
                Sent to <span className="font-semibold text-slate-700">
                  {useEmail ? emailVal : `+91 ${phone}`}
                </span>
              </p>

              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                    6-Digit OTP
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="_ _ _ _ _ _"
                    className="w-full px-4 py-3.5 border border-slate-300 rounded-lg text-2xl font-bold tracking-[0.4em] text-center focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all"
                    data-testid="otp-input"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                    Your Name <span className="text-slate-400 font-normal normal-case">(optional, for new accounts)</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all"
                    data-testid="name-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-btn"
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-300 text-white font-bold rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading
                    ? <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Verifying…</>
                    : "Verify & Continue"}
                </button>
              </form>

              <p className="text-center text-xs text-slate-400 mt-4">
                Didn't receive OTP?{" "}
                {resendCooldown > 0 ? (
                  <span className="text-slate-400">Resend in {resendCooldown}s</span>
                ) : (
                  <button
                    onClick={handleSendOTP}
                    disabled={loading}
                    data-testid="resend-btn"
                    className="text-blue-600 font-semibold hover:underline disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
